import React, { useEffect, useMemo, useState } from 'react';
import { FiClock, FiFilter, FiUsers } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import TourCard from '../components/TourCard';
import { tourService } from '../services/tourService';
import { EmptyState, ErrorState, LoadingState, PageHero, SectionHeading } from '../components/ui/PageElements';

const TourListPage = () => {
  const { t } = useTranslation();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minimumCapacity, setMinimumCapacity] = useState('');

  useEffect(() => {
    const fetchTours = async () => {
      try {
        setLoading(true);
        const data = await tourService.getAllTours();
        setTours(data || []);
        setError('');
      } catch (requestError) {
        console.error('Tour list error:', requestError);
        setError(t('design.tourBrowse.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchTours();
  }, [t]);

  const filteredTours = useMemo(() => {
    return tours.filter((tour) => {
      const priceMatch = !maxPrice || Number(tour.price) <= Number(maxPrice);
      const capacityMatch = !minimumCapacity || Number(tour.participantCount || 0) >= Number(minimumCapacity);
      return priceMatch && capacityMatch;
    });
  }, [maxPrice, minimumCapacity, tours]);

  if (loading) {
    return (
      <Layout>
        <LoadingState label={t('design.tourBrowse.loading')} />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <ErrorState title={t('design.tourBrowse.unavailableTitle')} message={error} action={<button type="button" onClick={() => window.location.reload()} className="btn-primary">{t('common.tryAgain')}</button>} />
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHero
        eyebrow={t('design.tourBrowse.heroEyebrow')}
        title={t('design.tourBrowse.heroTitle')}
        description={t('design.tourBrowse.heroDescription')}
        aside={
          <div className="soft-panel p-6 sm:p-8">
            <p className="eyebrow">{t('design.tourBrowse.filtersEyebrow')}</p>
            <div className="mt-6 space-y-4">
              <div>
                <label className="field-label">{t('design.tourBrowse.maxBudget')}</label>
                <input
                  type="number"
                  value={maxPrice}
                  onChange={(event) => setMaxPrice(event.target.value)}
                  className="input-shell"
                  placeholder="1500"
                />
              </div>
              <div>
                <label className="field-label">{t('design.tourBrowse.minCapacity')}</label>
                <input
                  type="number"
                  value={minimumCapacity}
                  onChange={(event) => setMinimumCapacity(event.target.value)}
                  className="input-shell"
                  placeholder="10"
                />
              </div>
            </div>
          </div>
        }
      />

      <section className="content-width pb-12">
        <SectionHeading
          eyebrow={t('design.tourBrowse.resultsEyebrow')}
          title={t('design.tourBrowse.resultsTitle', { count: filteredTours.length })}
          description={t('design.tourBrowse.resultsDescription')}
          action={
            <div className="flex flex-wrap gap-2">
              <span className="pill"><FiFilter /> {t('design.tourBrowse.pills.liveFiltering')}</span>
              <span className="pill"><FiClock /> {t('design.tourBrowse.pills.itinerary')}</span>
              <span className="pill"><FiUsers /> {t('design.tourBrowse.pills.capacity')}</span>
            </div>
          }
        />

        {filteredTours.length ? (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {filteredTours.map((tour) => (
              <TourCard key={tour.id} tour={tour} />
            ))}
          </div>
        ) : (
          <EmptyState title={t('design.tourBrowse.noResultsTitle')} description={t('design.tourBrowse.noResultsDescription')} />
        )}
      </section>
    </Layout>
  );
};

export default TourListPage;
